import { useEffect } from 'react';
import { CallbackDoc } from './callbackDocs';

interface CallbackDocsModalProps {
  doc: CallbackDoc | null;
  onClose: () => void;
}

export function CallbackDocsModal({ doc, onClose }: CallbackDocsModalProps) {
  // Close on Escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (doc) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [doc, onClose]);

  if (!doc) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-black/30"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          className="max-h-[80vh] w-full max-w-2xl overflow-y-auto rounded-lg bg-[var(--co-surface)] shadow-lg"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="sticky top-0 border-b border-[var(--co-border)] bg-[var(--co-surface)] p-4">
            <div className="flex items-center justify-between gap-4">
              <div>
                <div className="text-xs font-mono text-[var(--co-muted)]">{doc.method}</div>
                <h2 className="text-xl font-bold text-[var(--co-text)]">{doc.name}</h2>
                <p className="mt-1 text-sm text-[var(--co-muted)]">{doc.description}</p>
              </div>
              <button
                onClick={onClose}
                className="shrink-0 text-xl font-semibold text-[var(--co-muted)] hover:text-[var(--co-text)]"
              >
                ×
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="space-y-4 p-4">
            {/* URL Pattern */}
            <div>
              <h3 className="mb-2 text-sm font-semibold text-[var(--co-text)]">Endpoint</h3>
              <div className="rounded bg-[var(--co-bg)] p-3 font-mono text-xs text-[var(--co-text)]">
                {doc.method} {'{callback_base_url}'}
                {doc.defaultPath}
              </div>
            </div>

            {/* Request Body */}
            {doc.requestBody && (
              <div>
                <h3 className="mb-2 text-sm font-semibold text-[var(--co-text)]">Request Body</h3>
                <pre className="overflow-x-auto rounded bg-[var(--co-bg)] p-3 font-mono text-xs text-[var(--co-text)]">
                  {doc.requestBody}
                </pre>
              </div>
            )}

            {/* Success Response */}
            <div>
              <h3 className="mb-2 text-sm font-semibold text-[var(--co-text)]">Success Response (status: 0)</h3>
              <pre className="overflow-x-auto rounded bg-[var(--co-bg)] p-3 font-mono text-xs text-[var(--co-text)]">
                {doc.successResponse}
              </pre>
            </div>

            {/* Error Response Shape */}
            <div>
              <h3 className="mb-2 text-sm font-semibold text-[var(--co-text)]">Error Response (status: 1)</h3>
              <pre className="overflow-x-auto rounded bg-[var(--co-bg)] p-3 font-mono text-xs text-[var(--co-text)]">
                {doc.errorResponseShape}
              </pre>
            </div>

            {/* Error Codes Table */}
            <div>
              <h3 className="mb-2 text-sm font-semibold text-[var(--co-text)]">Possible Error Codes</h3>
              <div className="overflow-x-auto rounded border border-[var(--co-border)]">
                <table className="w-full text-sm">
                  <thead className="border-b border-[var(--co-border)] bg-[var(--co-bg)]">
                    <tr>
                      <th className="px-3 py-2 text-left font-semibold text-[var(--co-text)]">Code</th>
                      <th className="px-3 py-2 text-left font-semibold text-[var(--co-text)]">ID</th>
                      <th className="px-3 py-2 text-left font-semibold text-[var(--co-text)]">Meaning</th>
                    </tr>
                  </thead>
                  <tbody>
                    {doc.errorCodes.map((err, idx) => (
                      <tr
                        key={`${err.code}-${idx}`}
                        className="border-b border-[var(--co-border)] last:border-b-0"
                      >
                        <td className="px-3 py-2 font-mono text-[var(--co-text)]">{err.code}</td>
                        <td className="px-3 py-2 font-mono text-[var(--co-muted)]">{err.id ?? '—'}</td>
                        <td className="px-3 py-2 text-[var(--co-muted)]">{err.meaning}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Headers Info */}
            <div>
              <h3 className="mb-2 text-sm font-semibold text-[var(--co-text)]">Request Headers</h3>
              <div className="space-y-2 rounded bg-[var(--co-bg)] p-3 font-mono text-xs text-[var(--co-text)]">
                <div>
                  <div className="font-semibold">X-Repackz-Timestamp</div>
                  <div className="text-[var(--co-muted)]">Unix seconds when the request was signed</div>
                </div>
                <div>
                  <div className="font-semibold">X-Repackz-Signature</div>
                  <div className="text-[var(--co-muted)]">
                    sha256=HMAC(api_secret, "{'{timestamp}.{METHOD} {fullpath}.{raw_body}'}")
                  </div>
                  <div className="mt-1 text-[var(--co-muted)]">
                    <em>fullpath</em> is the request URI (path + query, no host); <em>raw_body</em> is the literal body bytes received.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
